import {Placement} from '@popperjs/core';
import React, {FC, PropsWithChildren, RefObject} from 'react';
import {Popper, PopperArrowProps} from 'react-popper';
import styled from 'styled-components';

import {globalWindow} from '../../../../helpers/src/browser/browserHelpers';
import {getWindow} from '../../../../helpers/src/browser/domHelpers';
import {SizeListener} from '../../hooks/sizeHooks';
import {boxShadow} from '../../theme/themeStyleHelpers';
import {Layer} from '../layers/layer';
import {useLayer} from '../layers/layerContext';
import {buildReferenceObject} from './repositionHelpers';

/*
 * Constants.
 */

const popoverHeight = 335;
export const popoverWidth = 310;
const minArrowTop = 30;
const maxArrowTop = popoverHeight - 8;

/*
 * Props.
 */

export interface PreviewRepositionProps {
  /** Ref to the anchor, or bounds provider. */
  anchor: RefObject<HTMLElement>;
  /** Placement position of the preview relative to the anchor */
  popperPlacement: Placement;
  /** Whether we should hide the arrow from the anchor to the popper */
  shouldHideArrow?: boolean;
}

/*
 * Style.
 */

interface ArrowContainerStyleProps {
  $placement: Placement;
}
const StyledArrowDiv = styled.div`
  width: 0;
  height: 0;
  border: 10px solid ${(p) => p.theme.greys.black};
  border-color: transparent transparent ${(p) => p.theme.greys.white} ${(p) => p.theme.greys.white};
  border-bottom-left-radius: 4px;
  ${(p) => boxShadow(p.theme, `0 0 3px ${p.theme.alphas.black30}, 0 0 1px ${p.theme.alphas.black10}`)};
`;

const StyledArrowContainerDiv = styled.div<ArrowContainerStyleProps>`
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  clip-path: polygon(0% 0%, 0% 100%, 100% 100%);
  ${(p) => makeStyleForArrowContainer(p.$placement)};
`;

interface ChildrenContainerStyleProps {
  $placement: Placement;
  $shouldHideArrow: boolean;
}
const StyledChildrenContainerDiv = styled.div<ChildrenContainerStyleProps>`
  ${(p) => makeStyleForChildrenContainer(p.$placement, p.$shouldHideArrow)};
`;

/*
 * Component.
 */

/** Position content next to an existing element. */
export const PreviewReposition: FC<PropsWithChildren<PreviewRepositionProps>> = (props) => {
  const {window} = useLayer();
  const {anchor, children, popperPlacement, shouldHideArrow} = props;
  const shouldHideArrowBoolean = Boolean(shouldHideArrow);

  if (!shouldDisplayPopper(anchor)) {
    return null;
  }

  return (
    <Layer>
      <Popper
        referenceElement={buildReferenceObject(anchor)}
        placement={popperPlacement}
        modifiers={[
          {
            name: 'preventOverflow',
            options: {
              padding: 0,
              boundary: window.document.body,
            },
          },
          {
            name: 'arrow',
            enabled: false,
          },
          {
            name: 'computeStyles',
            options: {
              gpuAcceleration: false,
            },
          },
        ]}
      >
        {({ref, style, forceUpdate, placement, arrowProps}) => {
          const finalPlacement = placement || popperPlacement;
          return (
            <div ref={ref} style={style} data-placement={placement}>
              <SizeListener onSize={forceUpdate}>
                <StyledChildrenContainerDiv
                  $placement={finalPlacement}
                  $shouldHideArrow={shouldHideArrowBoolean}
                >
                  {children}
                </StyledChildrenContainerDiv>
              </SizeListener>
              {maybeRenderArrow(shouldHideArrowBoolean, finalPlacement, arrowProps)}
            </div>
          );
        }}
      </Popper>
    </Layer>
  );
};

/*
 * Helpers.
 */

function shouldDisplayPopper(anchor: RefObject<HTMLElement>) {
  const element = anchor.current;
  if (!element) {
    return false;
  }

  // Is there enough horizontal space to fit the popper?
  const {left, right} = element.getBoundingClientRect();
  const window = getWindow(element) || globalWindow;
  const screenWidth = window.innerWidth;

  return left >= popoverWidth || screenWidth - right >= popoverWidth;
}

function constrainArrowPositioning(style?: React.CSSProperties) {
  const top = Number(style?.top);

  // No need to update the style if top is not a number.
  if (isNaN(top)) {
    return style;
  }

  // The top should be padded from the top/bottom to prevent interference with rounded corners.
  const constrainedTop = Math.min(Math.max(minArrowTop, top), maxArrowTop);
  return {
    ...style,
    top: constrainedTop,
  };
}

function computeArrowStyleForPlacement(placement: Placement) {
  switch (placement) {
    case 'auto-start':
    case 'auto':
    case 'auto-end':
      throw new Error('Unexpected Popper Placement');

    case 'top-start':
    case 'top':
    case 'top-end':
      return {bottom: 0};

    case 'right-start':
    case 'right':
    case 'right-end':
      return {left: 0};

    case 'bottom-start':
    case 'bottom':
    case 'bottom-end':
      return {top: 0};

    case 'left-start':
    case 'left':
    case 'left-end':
      return {right: 0};

    default:
      throw new Error(`Unknown Popper Placement ${placement}`);
  }
}

function maybeRenderArrow(shouldHideArrow: boolean, placement: Placement, arrowProps: PopperArrowProps) {
  if (shouldHideArrow) {
    return null;
  }

  return (
    <div
      data-popper-arrow
      style={{
        position: 'absolute',
        ...constrainArrowPositioning(arrowProps.style),
        ...computeArrowStyleForPlacement(placement),
      }}
      ref={arrowProps.ref}
    >
      <StyledArrowContainerDiv $placement={placement}>
        <StyledArrowDiv />
      </StyledArrowContainerDiv>
    </div>
  );
}

// Note: top/bottom arrows haven't been implemented yet
// Margin values come from the hypotenuse of the width (25) and height (25)
// with adjustments for differences between how margin right/left work
function makeStyleForArrowContainer(placement: Placement) {
  if (placement.startsWith('right')) {
    return `
      margin-left: 8.35px;
      transform: rotate(45deg);
    `;
  }

  if (placement.startsWith('left')) {
    return `
      margin-left: -33.35px;
      transform: rotate(225deg);
    `;
  }

  return '';
}

function makeStyleForChildrenContainer(placement: Placement, shouldHideArrow: boolean) {
  if (shouldHideArrow) {
    return '';
  }

  // Set margins to accommodate the space for the arrow
  if (placement.startsWith('right')) {
    return `
      margin-left: 20px;
    `;
  }

  if (placement.startsWith('left')) {
    return `
      margin-right: 20px;
    `;
  }

  return '';
}
